/**
 * Surface detail derived from the reference art.
 *
 * The source renders have their lighting baked in, which is useless to us —
 * everything in ABYSS is lit at runtime by the searchlight, depth extinction
 * and the sonar shell. So we throw the lighting away: convert to luminance,
 * high-pass out the low frequencies (which *are* the baked lighting), and keep
 * only the surface grain. That grain then drives an albedo modulation and a
 * derived normal, so the art contributes texture without contributing light.
 *
 * Packed per texel: R = detail (0.5 neutral), G/B = normal.xy, A = cavity.
 */

import { brushedMetal, paintedPlate } from './detailGen';

const SIZE = 256;

export interface DetailTextures {
  texture: GPUTexture;
  view: GPUTextureView;
  sampler: GPUSampler;
}

/**
 * Layer indices. 0..2 are loaded from art in `SOURCES` order; 3 and 4 are
 * generated at boot by `detailGen.ts` and cost no download.
 */
export const DETAIL = {
  rock: 0, metal: 1, crust: 2, brushed: 3, painted: 4,
} as const;

const SOURCES = ['/tex/rock-detail.png', '/tex/metal-detail.png', '/tex/crust-detail.png'];

/** Separable box blur over a single-channel image. */
function blur(src: Float32Array, w: number, h: number, radius: number): Float32Array {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  const norm = 1 / (radius * 2 + 1);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let s = 0;
      for (let k = -radius; k <= radius; k++) {
        // Mirror at the edges so the high-pass doesn't darken the border.
        let sx = x + k;
        if (sx < 0) sx = -sx;
        if (sx >= w) sx = w - 2 - (sx - w);
        s += src[y * w + sx];
      }
      tmp[y * w + x] = s * norm;
    }
  }
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      let s = 0;
      for (let k = -radius; k <= radius; k++) {
        let sy = y + k;
        if (sy < 0) sy = -sy;
        if (sy >= h) sy = h - 2 - (sy - h);
        s += tmp[sy * w + x];
      }
      out[y * w + x] = s * norm;
    }
  }
  return out;
}

function buildLayer(pixels: Uint8ClampedArray, w: number, h: number): Uint8Array<ArrayBuffer> {
  const lum = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) {
    const o = i * 4;
    const a = pixels[o + 3] / 255;
    const l = (pixels[o] * 0.2126 + pixels[o + 1] * 0.7152 + pixels[o + 2] * 0.0722) / 255;
    // Transparent regions of the cut-out get the mid value so they stay neutral.
    lum[i] = a > 0.5 ? l : 0.5;
  }

  // High-pass: removing the low frequencies removes the baked key light.
  const low = blur(lum, w, h, 14);
  const detail = new Float32Array(w * h);
  let min = Infinity, max = -Infinity;
  for (let i = 0; i < w * h; i++) {
    const d = lum[i] - low[i];
    detail[i] = d;
    if (d < min) min = d;
    if (d > max) max = d;
  }
  // Normalise to full range so different crops contribute equally.
  const scale = 0.5 / Math.max(1e-4, Math.max(Math.abs(min), Math.abs(max)));
  for (let i = 0; i < w * h; i++) detail[i] = detail[i] * scale + 0.5;

  // Cavity: a wider high-pass, used to darken crevices.
  const cav = blur(detail, w, h, 3);

  const out = new Uint8Array(w * h * 4);
  const at = (x: number, y: number) =>
    detail[((y + h) % h) * w + ((x + w) % w)];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      // Sobel on the detail height gives a tangent-space normal.
      const dx =
        at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1) -
        at(x - 1, y - 1) - 2 * at(x - 1, y) - at(x - 1, y + 1);
      const dy =
        at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1) -
        at(x - 1, y - 1) - 2 * at(x, y - 1) - at(x + 1, y - 1);
      const o = i * 4;
      out[o] = Math.round(detail[i] * 255);
      out[o + 1] = Math.round(Math.max(0, Math.min(1, -dx * 1.6 + 0.5)) * 255);
      out[o + 2] = Math.round(Math.max(0, Math.min(1, -dy * 1.6 + 0.5)) * 255);
      out[o + 3] = Math.round(Math.max(0, Math.min(1, cav[i])) * 255);
    }
  }
  return out;
}

/** Box-downsample an RGBA level by half. */
function halve(src: Uint8Array<ArrayBuffer>, w: number, h: number): { data: Uint8Array<ArrayBuffer>; w: number; h: number } {
  const nw = Math.max(1, w >> 1), nh = Math.max(1, h >> 1);
  const out = new Uint8Array(nw * nh * 4);
  for (let y = 0; y < nh; y++) {
    for (let x = 0; x < nw; x++) {
      for (let c = 0; c < 4; c++) {
        const a = src[((y * 2) * w + x * 2) * 4 + c];
        const b = src[((y * 2) * w + Math.min(w - 1, x * 2 + 1)) * 4 + c];
        const d = src[(Math.min(h - 1, y * 2 + 1) * w + x * 2) * 4 + c];
        const e = src[(Math.min(h - 1, y * 2 + 1) * w + Math.min(w - 1, x * 2 + 1)) * 4 + c];
        out[(y * nw + x) * 4 + c] = (a + b + d + e) >> 2;
      }
    }
  }
  return { data: out, w: nw, h: nh };
}

/** Fallback layer used if the art is missing — procedural grain, no lighting. */
function proceduralLayer(seed: number): Uint8Array<ArrayBuffer> {
  const px = new Uint8ClampedArray(SIZE * SIZE * 4);
  let s = seed | 1;
  const rnd = () => {
    s = (Math.imul(s ^ (s >>> 15), 2246822519) + 3266489917) | 0;
    return ((s >>> 0) % 1000) / 1000;
  };
  for (let i = 0; i < SIZE * SIZE; i++) {
    const v = 90 + rnd() * 120;
    px[i * 4] = px[i * 4 + 1] = px[i * 4 + 2] = v;
    px[i * 4 + 3] = 255;
  }
  return buildLayer(px, SIZE, SIZE);
}

/**
 * How long a single detail texture gets before the procedural fallback wins.
 *
 * There was no limit at all. Each source was fetched and decoded inside a
 * try/catch that handled every FAILURE beautifully and could not handle a HANG:
 * `fetch` does not time out on its own, so a request that never settles — a
 * flaky phone connection, a service worker holding the response — parks boot on
 * "READING SURFACE DATA" forever. The player then waits out the startup
 * watchdog and is told their browser lacks WebGPU, which is both wrong and
 * unrelated. A hang has to degrade like a failure, because to the player it is
 * one.
 */
const LOAD_TIMEOUT_MS = 8000;

function withTimeout<T>(work: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`timed out after ${ms}ms: ${what}`)), ms);
    work.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

async function loadOne(url: string): Promise<Uint8Array<ArrayBuffer>> {
  const res = await fetch(url, { cache: 'force-cache' });
  if (!res.ok) throw new Error(`${res.status}`);
  const bmp = await createImageBitmap(await res.blob());
  const canvas = new OffscreenCanvas(SIZE, SIZE);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(bmp, 0, 0, SIZE, SIZE);
  bmp.close();
  return buildLayer(ctx.getImageData(0, 0, SIZE, SIZE).data, SIZE, SIZE);
}

/**
 * `maxAnisotropy` is the player's ANISOTROPIC FILTERING setting, passed in
 * rather than read here because this module has no business knowing about
 * saved settings — and it is a build-time property of the sampler, so changing
 * it needs a fresh renderer. See `Settings.gfxAf`.
 *
 * WebGPU has no query for the highest value the adapter supports; it clamps
 * silently, so asking for 16 on hardware that caps at 8 is safe and gives 8.
 * The spec does require all three filters to be 'linear' when this is above 1,
 * which they are below.
 */
export async function loadDetailTextures(
  device: GPUDevice, maxAnisotropy = 8,
): Promise<DetailTextures> {
  const layers: Uint8Array<ArrayBuffer>[] = [];

  for (const url of SOURCES) {
    try {
      layers.push(await withTimeout(loadOne(url), LOAD_TIMEOUT_MS, url));
    } catch (err) {
      console.warn(`[abyss] detail texture ${url} unavailable, using procedural grain`, err);
      layers.push(proceduralLayer(0x9e37 + layers.length * 7919));
    }
  }

  /**
   * THE GENERATED HALF OF THE SET, and it goes through the identical packing.
   *
   * `buildLayer` is the whole point of putting them here rather than shipping
   * two more PNGs: it high-passes, takes a Sobel normal and derives the cavity,
   * so a computed height field and a photographed one arrive at the shader as
   * the same kind of object. There is no second code path to keep in step.
   *
   * No `try`, no timeout, no fallback. These cannot fail the way a fetch can —
   * there is nothing to fetch. See `detailGen.ts` for why they are computed at
   * all rather than drawn.
   */
  layers.push(buildLayer(brushedMetal(), SIZE, SIZE));
  layers.push(buildLayer(paintedPlate(), SIZE, SIZE));

  const mipCount = Math.floor(Math.log2(SIZE)) + 1;
  const texture = device.createTexture({
    label: 'detail',
    size: [SIZE, SIZE, layers.length],
    format: 'rgba8unorm',
    mipLevelCount: mipCount,
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
  });

  layers.forEach((base, layer) => {
    let data = base, w = SIZE, h = SIZE;
    for (let level = 0; level < mipCount; level++) {
      device.queue.writeTexture(
        { texture, mipLevel: level, origin: { x: 0, y: 0, z: layer } },
        data,
        { bytesPerRow: w * 4, rowsPerImage: h },
        { width: w, height: h, depthOrArrayLayers: 1 },
      );
      if (level < mipCount - 1) {
        const next = halve(data, w, h);
        data = next.data; w = next.w; h = next.h;
      }
    }
  });

  return {
    texture,
    view: texture.createView({ dimension: '2d-array' }),
    // Mirrored repeat hides the seam; the high-pass leaves the mean at 0.5,
    // so mirrored tiles join without a visible ridge.
    sampler: device.createSampler({
      magFilter: 'linear',
      minFilter: 'linear',
      mipmapFilter: 'linear',
      addressModeU: 'mirror-repeat',
      addressModeV: 'mirror-repeat',
      maxAnisotropy,
    }),
  };
}
